import React, { useState, useEffect } from 'react';
import { Cloud, CloudOff, Trash2, RefreshCw, X } from 'lucide-react';
import { getPendingActions, removePendingAction } from '../utils/offlineDb';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

interface PendingAction {
  id: number;
  type: string;
  data: any;
  timestamp: number;
}

interface SyncManagerProps {
  isOffline: boolean;
  pendingActions: number;
  onRefresh: () => void;
}

function getActionDescription(action: PendingAction): string {
  switch (action.type) {
    case 'clockIn':
      return 'Clock in';
    case 'finishJob':
      return 'Finish job';
    case 'clockOut':
      return 'Clock out';
    case 'startTask':
      return 'Start task';
    case 'endTask':
      return 'Complete task';
    case 'addExpense':
      return 'Add expense';
    default:
      return action.type;
  }
}

export function SyncManager({ isOffline, pendingActions, onRefresh }: SyncManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [actions, setActions] = useState<PendingAction[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPendingActions();
    }
  }, [isOpen]);

  const loadPendingActions = async () => {
    try {
      const actions = await getPendingActions();
      setActions(actions);
    } catch (error) {
      console.error('Failed to load pending actions:', error);
      toast.error('Failed to load pending actions');
    }
  };

  const handleRemoveAction = async (id: number) => {
    setLoading(true);
    try {
      const actionToRemove = actions.find(a => a.id === id);
      if (!actionToRemove) {
        throw new Error('Action not found');
      }

      // Validate dependencies
      if (actionToRemove.type === 'clockOut' || actionToRemove.type === 'finishJob') {
        const hasClockIn = actions.some(a => 
          a.type === 'clockIn' && 
          a.data.worker_id === actionToRemove.data.worker_id
        );
        if (hasClockIn) {
          toast.error('Cannot remove this action while clock-in is pending');
          return;
        }
      }

      await removePendingAction(id);
      await loadPendingActions();
      onRefresh();
      toast.success('Action removed successfully');
    } catch (error) {
      console.error('Failed to remove action:', error);
      toast.error(error.message || 'Failed to remove action');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg p-1 transition-colors"
      >
        {isOffline ? (
          <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
            <CloudOff className="h-4 w-4" />
            {pendingActions > 0 && (
              <span className="text-xs font-medium">
                {pendingActions} pending
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <Cloud className="h-4 w-4" />
            {pendingActions > 0 && (
              <span className="text-xs font-medium">
                Syncing...
              </span>
            )}
          </div>
        )}
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-light-card dark:bg-dark-card rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Pending Actions
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {actions.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No pending actions
              </div>
            ) : (
              <div className="space-y-4">
                {actions.map((action) => (
                  <div
                    key={action.id}
                    className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {getActionDescription(action)}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {format(action.timestamp, 'MMM d, h:mm a')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveAction(action.id)}
                        disabled={loading}
                        className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 p-1 rounded transition-colors"
                      >
                        {loading ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}